import React from "react";

function formatYears(year) {
  return Array.isArray(year) ? year.join(", ") : year || "-";
}

function octagonClip(cut) {
  return `polygon(${cut}px 0, calc(100% - ${cut}px) 0, 100% ${cut}px, 100% calc(100% - ${cut}px), calc(100% - ${cut}px) 100%, ${cut}px 100%, 0 calc(100% - ${cut}px), 0 ${cut}px)`;
}

function RecordRow({ item }) {
  return (
    <div
      className="flex items-center justify-between gap-4 px-5 py-4"
      style={{ borderColor: "rgba(230, 198, 122, 0.12)" }}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="h-8 w-0.5 flex-shrink-0 rounded-full opacity-50" style={{ background: "var(--accent)" }} />
        <div className="min-w-0">
          <p className="truncate font-semibold leading-tight text-[var(--text-main)]">{item.owner_name}</p>
          <p className="mt-0.5 text-xs text-[var(--muted-text)]">{formatYears(item.year)}</p>
        </div>
      </div>
      <span className="theme-display flex-shrink-0 text-2xl font-semibold sm:text-3xl" style={{ color: "var(--accent-soft)" }}>
        {item.value}
      </span>
    </div>
  );
}

// Dark bronze body with a polished brass nameplate header
function RecordPlaque({ title, children }) {
  return (
    <div
      className="h-full rounded-[1.5rem] border p-2 shadow-2xl shadow-black/25"
      style={{
        borderColor: "rgba(120, 88, 40, 0.9)",
        background: "linear-gradient(135deg, #6b3924 0%, #7a3e28 45%, #5d2f1f 100%)",
        boxShadow: "inset 0 0 0 1px rgba(255, 232, 192, 0.06)",
      }}
    >
      <div className="relative p-[2px]" style={{ clipPath: octagonClip(22), background: "rgba(230, 198, 122, 0.85)" }}>
        <div className="relative" style={{ clipPath: octagonClip(20), background: "linear-gradient(180deg, #2e1f10 0%, #1c1209 100%)" }}>
          <div className="pointer-events-none absolute left-4 top-4 z-10 h-2 w-2 rounded-full" style={{ background: "rgba(241, 212, 136, 0.98)", boxShadow: "0 0 0 1px rgba(120, 79, 30, 0.55)" }} />
          <div className="pointer-events-none absolute right-4 top-4 z-10 h-2 w-2 rounded-full" style={{ background: "rgba(241, 212, 136, 0.98)", boxShadow: "0 0 0 1px rgba(120, 79, 30, 0.55)" }} />

          <div
            className="relative px-5 py-4"
            style={{
              background: "linear-gradient(135deg, #f3d98a 0%, #d4ad5b 50%, #a8793a 100%)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -2px 4px rgba(60,40,10,0.35)",
            }}
          >
            <h2 className="text-center text-base font-semibold leading-snug sm:text-lg" style={{ color: "#2a1a0c" }}>
              {title}
            </h2>
          </div>

          <div className="relative divide-y" style={{ borderColor: "rgba(230, 198, 122, 0.1)" }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

const CATEGORY_ORDER = [
  "Best Regular Season Record",
  "Worst Regular Season Record",
  "Most Points Scored in a Game",
  "Least Points Scored in a Game",
  "Most Points Scored in a Season",
  "Least Points Scored in a Season",
  "Most Points Against in a Season",
  "Least Points Against in a Season",
];

export default function RecordTable({ records }) {
  const grouped = records.reduce((acc, rec) => {
    if (!acc[rec.category]) acc[rec.category] = [];
    acc[rec.category].push(rec);
    return acc;
  }, {});

  const ordered = [
    ...CATEGORY_ORDER.filter((c) => grouped[c]),
    ...Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  const isLastOdd = ordered.length % 2 === 1;

  return (
    <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
      {ordered.map((category, index) => {
        const isLast = index === ordered.length - 1;
        return (
          <div
            key={category}
            className={isLastOdd && isLast ? "lg:col-span-2 lg:mx-auto lg:w-full lg:max-w-[48%]" : ""}
          >
            <RecordPlaque title={category}>
              {(grouped[category] || []).map((item, i) => (
                <RecordRow key={`${category}-${i}`} item={item} />
              ))}
            </RecordPlaque>
          </div>
        );
      })}
    </div>
  );
}
